// Shared overlay scaffolding for the app's modals (how-to, suggest, rename,
// confirm, share, leaderboard notice). Every modal needs the same ceremony:
// a backdrop that closes on click-outside, Escape-to-close, and listener
// cleanup on close. Centralising it here keeps that behaviour — and future
// accessibility work like focus trapping — a one-place change, instead of the
// same ~15 lines copy-pasted per dialog.

export interface ModalHandle {
  /** The backdrop element; the modal markup is its only child. */
  readonly overlay: HTMLElement;
  /** Remove the modal and detach its listeners. Idempotent-safe to call once. */
  readonly close: () => void;
}

/**
 * Mount `innerHtml` (a `<div class="modal">…</div>`) inside a backdrop overlay
 * and wire the shared close behaviour. Callers own the modal's own buttons via
 * `overlay.querySelector(...)`; `onClose` fires after any dismissal (button,
 * Escape, or backdrop click).
 */
export function openModal(innerHtml: string, opts: { onClose?: () => void } = {}): ModalHandle {
  const overlay = document.createElement("div");
  overlay.className = "modalOverlay";
  overlay.innerHTML = innerHtml;
  const close = (): void => {
    overlay.remove();
    document.removeEventListener("keydown", onKey);
    opts.onClose?.();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey);
  // Click on the backdrop (but not the dialog itself) dismisses.
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  document.body.appendChild(overlay);
  return { overlay, close };
}
